/**
 * Puzzle memoisation.
 *
 * Generation is deterministic and fast, but the archive asks for dozens of
 * days at once and a mid-range phone should not regenerate yesterday's puzzle
 * every time the list is scrolled. A small LRU keeps the recent window warm.
 */

import { GEN_VERSION } from '../core/schedule';
import { Puzzle, generatePuzzle } from '../core/generator';

const LIMIT = 120;
const cache = new Map<number, Puzzle>();

/**
 * Seed the cache from the static file `npm run pregenerate` wrote.
 *
 * Every puzzle in it is byte-identical to what local generation would produce,
 * so a partial or stale file is harmless: days outside its horizon simply fall
 * back to generating. Failures are ignored entirely — an unseeded cache costs
 * milliseconds, not correctness.
 */
export async function loadPregenerated(): Promise<void> {
  try {
    const response = await fetch('./puzzles.json');
    if (!response.ok) return;
    const data = (await response.json()) as {
      version?: number;
      start?: number;
      puzzles?: Puzzle[];
    };
    if (data.version !== GEN_VERSION || typeof data.start !== 'number') return;
    if (!Array.isArray(data.puzzles)) return;

    data.puzzles.forEach((puzzle, i) => {
      if (puzzle && typeof puzzle.index === 'number') cache.set(data.start! + i, puzzle);
    });
  } catch {
    /* no file, or storage blocked. Local generation covers it. */
  }
}

export function puzzleFor(index: number): Puzzle {
  const day = Math.max(0, Math.floor(index));
  const hit = cache.get(day);
  if (hit) {
    // Refresh recency.
    cache.delete(day);
    cache.set(day, hit);
    return hit;
  }

  const puzzle = generatePuzzle(day);
  cache.set(day, puzzle);
  while (cache.size > LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
  return puzzle;
}

export function isCached(index: number): boolean {
  return cache.has(index);
}

/**
 * Warm the cache off the critical path so the first tap on Play is instant.
 * Safe to call repeatedly; failures are ignored, because an un-warmed cache
 * only costs a few milliseconds.
 */
export function prewarm(indices: number[]): void {
  const job = () => {
    for (const index of indices) {
      if (!cache.has(index)) puzzleFor(index);
    }
  };
  if (typeof requestIdleCallback === 'function') requestIdleCallback(job, { timeout: 1500 });
  else setTimeout(job, 60);
}
